import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import IILSubnav from "../components/IILSubnav";
import { advancePositions, employeeBalances, isActiveAdvance, normalizeCode } from "../services/iilAdvanceCalculator";
import { downloadLedgerFile, downloadPaymentFile, missingBankDetails } from "../services/iilAdvanceExport";
import {
  createAdvance, loadAdvanceLedger, loadEmployeeMaster, markExported, markPaid, rejectAdvance
} from "../services/iilAdvanceService";

const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;
const todayIso = () => new Date().toISOString().slice(0, 10);
const dateLabel = (date) => (date ? new Date(`${date}T00:00:00Z`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "—");
const emptyForm = () => ({ amount: "", givenDate: todayIso(), emailRef: "", installment: "", note: "" });

export default function IILAdvances() {
  const [ledger, setLedger] = useState({ advances: [], recoveries: [] });
  const [master, setMaster] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [lastSaved, setLastSaved] = useState(null);
  const [checked, setChecked] = useState({});
  const [paid, setPaid] = useState({ date: todayIso(), reference: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [loaded, employees] = await Promise.all([loadAdvanceLedger(), loadEmployeeMaster()]);
      setLedger(loaded); setMaster(employees);
    } catch (err) { setError(err.message || "Could not load advances."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const notify = (text, isError = false) => { setError(isError ? text : ""); setMessage(isError ? "" : text); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const positions = useMemo(() => advancePositions({ advances: ledger.advances, recoveries: ledger.recoveries }), [ledger]);
  const balances = useMemo(() => [...employeeBalances(positions).values()].sort((a, b) => b.outstanding - a.outstanding), [positions]);
  const masterByCode = useMemo(() => new Map(master.map((employee) => [normalizeCode(employee.employeeCode), employee])), [master]);
  const outstandingTotal = balances.reduce((sum, balance) => sum + balance.outstanding, 0);
  const toPay = ledger.advances.filter((advance) => isActiveAdvance(advance) && advance.paymentStatus !== "PAID");
  const notExported = toPay.filter((advance) => !advance.exportCount);

  const matches = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return [];
    return master.filter((employee) => employee.active !== false
      && [employee.employeeCode, employee.name, employee.clientEmpId, employee.segment].some((value) => String(value || "").toLowerCase().includes(query))).slice(0, 8);
  }, [master, search]);

  const set = (field) => (e) => setForm((current) => ({ ...current, [field]: e.target.value }));
  const bankMissing = selected ? missingBankDetails({ bank: selected.bank, account: selected.account }) : [];

  const outstandingByCode = useMemo(() => new Map(balances.map((balance) => [balance.code, balance.outstanding])), [balances]);

  const download = async (advances, label) => {
    downloadPaymentFile(advances, label, outstandingByCode);
    try { await markExported(advances.map((advance) => advance.id)); await load(); }
    catch (err) { notify(`The file was downloaded, but it could not be marked as exported: ${err.message}`, true); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!selected) { notify("Choose the employee first.", true); return; }
    setBusy(true);
    try {
      const saved = await createAdvance({
        ...form, employeeCode: selected.employeeCode, employeeName: selected.name, clientEmpId: selected.clientEmpId, segment: selected.segment,
        cnic: selected.cnic, bank: selected.bank, account: selected.account, pmtMode: selected.pmtMode,
        fatherName: selected.fatherName, department: selected.department, designation: selected.designation
      });
      setLastSaved(saved); setSelected(null); setSearch(""); setForm(emptyForm());
      await load();
      notify(`Advance of ${money(saved.amount)} saved for ${saved.employeeName}. It will be recovered automatically at payroll.`);
    } catch (err) { notify(err.message || "Could not save the advance.", true); }
    finally { setBusy(false); }
  };

  const chosenAdvances = toPay.filter((advance) => checked[advance.id]);

  const markChosenPaid = async () => {
    setBusy(true);
    try {
      await markPaid(chosenAdvances.map((advance) => advance.id), { paidDate: paid.date, bankReference: paid.reference });
      setChecked({}); await load(); notify(`${chosenAdvances.length} advance(s) marked as paid.`);
    } catch (err) { notify(err.message || "Could not mark as paid.", true); }
    finally { setBusy(false); }
  };

  const reject = async (advance) => {
    const reason = window.prompt(`Reason for rejecting the ${money(advance.amount)} advance to ${advance.employeeName}:`);
    if (reason === null) return;
    try { await rejectAdvance(advance, reason, ledger.recoveries); await load(); notify("Advance rejected."); }
    catch (err) { notify(err.message || "Could not reject.", true); }
  };

  const history = [...ledger.advances].sort((a, b) => `${b.givenDate} ${b.seq || 0}`.localeCompare(`${a.givenDate} ${a.seq || 0}`));
  const positionOf = (id) => positions.find((position) => position.advance.id === id);

  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">IIL Automation</div>
            <h1 className="page-title">Employee Advances</h1>
            <p className="page-copy">
              Enter an advance when Safdar's email arrives. Download the payment file with the employee's bank details for finance, and the
              advance is recovered automatically when you complete that month's IIL payroll.
            </p>
          </div>
        </header>
        <IILSubnav />

        {(message || error) && <div className={`management-alert ${error ? "error" : "success"}`}>{error || message}</div>}

        <section className="card-grid">
          <div className="metric-card"><div className="metric-label">Outstanding to recover</div><div className="metric-value">{money(outstandingTotal)}</div></div>
          <div className="metric-card"><div className="metric-label">Employees with a balance</div><div className="metric-value">{balances.filter((balance) => balance.outstanding > 0).length}</div></div>
          <div className="metric-card"><div className="metric-label">Waiting to be paid</div><div className="metric-value">{toPay.length}</div><div className="page-copy" style={{ marginTop: 6 }}>{notExported.length} payment file(s) not downloaded yet</div></div>
        </section>

        {lastSaved && (
          <div className="panel" style={{ padding: 16, marginBottom: 20, borderColor: "var(--success)" }}>
            <strong>Saved: {money(lastSaved.amount)} for {lastSaved.employeeName}.</strong>
            <p className="page-copy" style={{ margin: "6px 0 10px" }}>The Excel file has the complete data — employee, bank details, amount, recovery plan, Safdar's email reference and who entered it.</p>
            <div className="button-row">
              <button type="button" className="btn btn-primary" onClick={() => download([lastSaved], lastSaved.employeeCode)}>Download Complete Excel File</button>
              <button type="button" className="btn btn-muted" onClick={() => setLastSaved(null)}>Dismiss</button>
            </div>
          </div>
        )}

        <section className="panel">
          <div className="panel-header"><h2 className="panel-title">Record an advance</h2></div>
          <form className="panel-body" onSubmit={submit}>
            {!selected ? (
              <div className="field" style={{ maxWidth: 520 }}>
                <label>Find the employee (code, name or Safdar ID)</label>
                <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Start typing…" />
                {matches.map((employee) => (
                  <button key={employee.id} type="button" className="btn btn-muted" style={{ justifyContent: "flex-start", marginTop: 6 }} onClick={() => setSelected(employee)}>
                    {employee.employeeCode} — {employee.name}{employee.segment ? ` · ${employee.segment}` : ""}
                  </button>
                ))}
                {search.trim() && !matches.length && <p className="page-copy">No active employee matches. Add or reactivate them in IIL Attendance first.</p>}
              </div>
            ) : (
              <div className="panel" style={{ padding: 14, marginBottom: 16 }}>
                <strong>{selected.employeeCode} — {selected.name}</strong>
                <p className="page-copy" style={{ margin: "6px 0" }}>
                  {selected.segment || "No segment"} · CNIC {selected.cnic || "—"} · {selected.bank || "no bank"} · account {selected.account || "—"} · {selected.pmtMode || "payment mode not set"}
                </p>
                {bankMissing.length > 0 && <div className="management-alert error">Missing {bankMissing.join(" and ")} in the IIL Employee Master. The payment file will be incomplete — update the employee in IIL Attendance first.</div>}
                <button type="button" className="btn btn-muted" onClick={() => setSelected(null)}>Choose a different employee</button>
              </div>
            )}
            <div className="form-grid">
              <div className="field"><label>Advance amount (PKR)</label><input className="input" type="number" min="1" value={form.amount} onChange={set("amount")} /></div>
              <div className="field"><label>Date given</label><input className="input" type="date" value={form.givenDate} onChange={set("givenDate")} /></div>
              <div className="field"><label>Safdar's email (date / subject)</label><input className="input" value={form.emailRef} onChange={set("emailRef")} placeholder="e.g. 04-Oct — Advance request" /></div>
              <div className="field"><label>Recover per payroll (PKR) — blank = all at the next payroll</label><input className="input" type="number" min="1" value={form.installment} onChange={set("installment")} /></div>
              <div className="field field-wide"><label>Note</label><input className="input" value={form.note} onChange={set("note")} /></div>
            </div>
            <div className="button-row" style={{ marginTop: 16 }}>
              <button className="btn btn-primary" type="submit" disabled={busy || !selected}>Save Advance</button>
            </div>
          </form>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">Payment files — advances waiting to be paid</h2>
            <div className="button-row">
              <button type="button" className="btn btn-primary" disabled={!chosenAdvances.length} onClick={() => download(chosenAdvances, "selected")}>Download Selected ({chosenAdvances.length})</button>
              <button type="button" className="btn btn-muted" disabled={!notExported.length} onClick={() => download(notExported, "new")}>Download All Not Yet Downloaded ({notExported.length})</button>
            </div>
          </div>
          <div className="panel-body table-wrap">
            <table className="data-table">
              <thead><tr><th /><th>Date</th><th>Employee</th><th>Bank / account</th><th>Amount</th><th>Safdar's email</th><th>File</th></tr></thead>
              <tbody>
                {toPay.sort((a, b) => b.seq - a.seq).map((advance) => {
                  const missing = missingBankDetails(advance);
                  return (
                    <tr key={advance.id}>
                      <td><input type="checkbox" checked={Boolean(checked[advance.id])} onChange={(e) => setChecked({ ...checked, [advance.id]: e.target.checked })} /></td>
                      <td>{dateLabel(advance.givenDate)}</td><td>{advance.employeeCode} — {advance.employeeName}</td>
                      <td>{[advance.bank, advance.account].filter(Boolean).join(" · ") || "—"}{missing.length > 0 && <div style={{ color: "var(--danger)", fontSize: 12 }}>missing {missing.join(", ")}</div>}</td>
                      <td>{money(advance.amount)}</td><td style={{ fontSize: 12 }}>{advance.emailRef || "—"}</td>
                      <td>{advance.exportCount ? <span className="status-pill success">Downloaded{advance.exportCount > 1 ? ` ×${advance.exportCount}` : ""}</span> : <span className="status-pill warning">Not downloaded</span>}</td>
                    </tr>
                  );
                })}
                {!toPay.length && <tr><td colSpan={7}>Nothing is waiting to be paid.</td></tr>}
              </tbody>
            </table>
            {chosenAdvances.length > 0 && (
              <div className="form-grid" style={{ marginTop: 14 }}>
                <div className="field"><label>Paid on</label><input className="input" type="date" value={paid.date} onChange={(e) => setPaid({ ...paid, date: e.target.value })} /></div>
                <div className="field"><label>Bank reference (optional)</label><input className="input" value={paid.reference} onChange={(e) => setPaid({ ...paid, reference: e.target.value })} /></div>
                <div className="field"><label>&nbsp;</label><button type="button" className="btn btn-success" disabled={busy} onClick={markChosenPaid}>Mark {chosenAdvances.length} as Paid</button></div>
              </div>
            )}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header"><h2 className="panel-title">Outstanding by employee</h2></div>
          <div className="panel-body table-wrap">
            <table className="data-table">
              <thead><tr><th>Employee</th><th>Advanced</th><th>Recovered</th><th>Outstanding</th><th /></tr></thead>
              <tbody>
                {balances.filter((balance) => balance.outstanding > 0).map((balance) => {
                  const inactive = masterByCode.get(balance.code)?.active === false;
                  return (
                    <tr key={balance.code}>
                      <td>{balance.code} — {balance.name}</td><td>{money(balance.given)}</td><td>{money(balance.recovered)}</td><td><strong>{money(balance.outstanding)}</strong></td>
                      <td>{inactive && <span className="status-pill danger">Inactive — recover before final settlement</span>}</td>
                    </tr>
                  );
                })}
                {!balances.some((balance) => balance.outstanding > 0) && <tr><td colSpan={5}>No advance is outstanding.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">All advances</h2>
            <button type="button" className="btn btn-primary" disabled={!ledger.advances.length} onClick={() => downloadLedgerFile({ advances: history, recoveries: ledger.recoveries, positions, balances })}>Download All Advances (Excel)</button>
          </div>
          <div className="panel-body table-wrap">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Employee</th><th>Amount</th><th>Recovery</th><th>Recovered</th><th>Status</th><th>Payment</th><th /></tr></thead>
              <tbody>
                {history.map((advance) => {
                  const rejected = !isActiveAdvance(advance);
                  const position = positionOf(advance.id);
                  return (
                    <tr key={advance.id} style={rejected ? { opacity: 0.55, textDecoration: "line-through" } : undefined}>
                      <td>{dateLabel(advance.givenDate)}</td><td>{advance.employeeCode} — {advance.employeeName}</td><td>{money(advance.amount)}</td>
                      <td>{advance.installment ? `${money(advance.installment)} per payroll` : "All at next payroll"}</td>
                      <td>{position ? money(position.recovered) : "—"}</td>
                      <td>{rejected ? <span style={{ textDecoration: "none" }}>Rejected: {advance.rejectedReason}</span> : position?.status === "CLEARED" ? <span className="status-pill success">Cleared</span> : position?.status === "PART_RECOVERED" ? <span className="status-pill warning">Part recovered</span> : <span className="status-pill warning">Open</span>}</td>
                      <td>{rejected ? "—" : advance.paymentStatus === "PAID" ? `Paid ${dateLabel(advance.paidDate)}${advance.bankReference ? ` (${advance.bankReference})` : ""}` : "To pay"}</td>
                      <td>{!rejected && <button className="btn btn-danger" type="button" onClick={() => reject(advance)}>Reject</button>}</td>
                    </tr>
                  );
                })}
                {!history.length && <tr><td colSpan={8}>No advances recorded yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
